import "./chartSetup";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import type { ChartData, ChartOptions } from "chart.js";
import { Chart } from "react-chartjs-2";
import { useFinance } from "../../FinanceContext";
import { moneyShort } from "../../data/model";
import { MONTHS } from "../../data/seedData";

export interface CashFlowRow {
  mn: string;
  rev: number;
  exp: number;
  net: number;
  cum: number;
}

export function CashFlowChart({ rows }: { rows: CashFlowRow[] }) {
  const { C } = useFinance();
  const theme = useTheme();
  const text = theme.palette.mode === "dark" ? "#9aa1b8" : "#69708a";

  const data = {
    labels: MONTHS,
    datasets: [
      {
        type: "bar" as const,
        label: "Net",
        data: rows.map((r) => Math.round(r.net * C.rate)),
        backgroundColor: rows.map((r) => (r.net >= 0 ? "#16a34a" : "#dc2626")),
        borderRadius: 4,
      },
      {
        type: "line" as const,
        label: "Cumulative",
        data: rows.map((r) => Math.round(r.cum * C.rate)),
        borderColor: "#5b53ff",
        tension: 0.3,
        pointRadius: 2,
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { color: text } },
      tooltip: {
        callbacks: {
          label: (c) => `${c.dataset.label}: ${moneyShort(Number(c.parsed.y), C.symbol)}`,
        },
      },
    },
    scales: {
      y: { ticks: { color: text, callback: (v) => moneyShort(Number(v), C.symbol) } },
      x: { ticks: { color: text } },
    },
  };
  return (
    <Box sx={{ position: "relative", height: 260 }}>
      {/* Mixed bar+line chart: chart.js supports per-dataset `type` overrides at runtime,
          but its TS types don't model a "bar chart with one line dataset" cleanly. */}
      <Chart type="bar" data={data as unknown as ChartData<"bar">} options={options} />
    </Box>
  );
}
